import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, User, Phone, Youtube, Filter, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import NoticeBanner from '@/components/NoticeBanner';
import ExitPopup from '@/components/ExitPopup';
import Seo from '@/components/Seo';
import { optimizeImage } from '@/utils/imageOptimize';
import ProtectedImage from '@/components/ProtectedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Products({ user, setUser, cartCount }) {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('all');
  const [subcategory, setSubcategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check URL params for category
    const urlCategory = searchParams.get('category');
    if (urlCategory) {
      setCategory(urlCategory);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [category, subcategory]);

  const fetchProducts = async () => {
    try {
      let url = `${BACKEND_URL}/api/products`;
      const params = [];
      if (category !== 'all') params.push(`category=${category}`);
      if (subcategory !== 'all') params.push(`subcategory=${subcategory}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await fetch(url);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setSubcategory('all');
  };

  // Client-side search over name / description / brand / SKU. Diacritic and
  // case-insensitive so "protesis" matches "prótesis".
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    const norm = (s) => (s || '').toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const nq = norm(q);
    return products.filter((p) => {
      const bag = [p.name, p.description, p.brand, p.sku, p.category, p.subcategory]
        .map(norm).join(' ');
      return bag.includes(nq);
    });
  }, [products, search]);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={
          category === 'protesis'
            ? 'Prótesis capilares a medida'
            : category === 'mantenimiento'
              ? 'Productos de mantenimiento capilar'
              : 'Catálogo de productos'
        }
        description={
          category === 'protesis'
            ? 'Prótesis capilares fabricadas a medida con las mejores calidades. Envío discreto a toda España.'
            : category === 'mantenimiento'
              ? 'Adhesivos, disolventes, cintas de doble cara y productos de mantenimiento capilar profesional.'
              : 'Catálogo completo de prótesis capilares y productos de mantenimiento.'
        }
        path={category !== 'all' ? `/productos?category=${category}` : '/productos'}
      />
      <ExitPopup />
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/productos" className="text-sm font-medium text-black">PRODUCTOS</Link>
              <Link to="/contacto-personalizado" className="text-sm font-medium text-gray-700 hover:text-black">CONTACTO</Link>
              <a href="https://www.youtube.com/nomascalvos" target="_blank" rel="noopener noreferrer"><Youtube className="w-5 h-5" /></a>
              <button onClick={() => window.open('https://wa.me/34649471889', '_blank')}><Phone className="w-5 h-5" /></button>
              <Link to="/checkout" className="relative"><ShoppingCart className="w-5 h-5" />{cartCount > 0 && <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>}</Link>
              {user && <Link to="/perfil"><User className="w-5 h-5" /></Link>}
            </nav>
          </div>
        </div>
      </header>

      {/* Notice Banner */}
      <NoticeBanner />

      <div className="container mx-auto px-6 py-12 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>Catálogo</h1>

          {/* Search bar */}
          <div className="relative mb-4 max-w-2xl" data-testid="products-search-wrapper">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, marca o descripción…"
              className="pl-10 pr-10 h-11 border-2 border-black"
              data-testid="products-search-input"
              aria-label="Buscar productos"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-black"
                aria-label="Limpiar búsqueda"
                data-testid="products-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <Filter className="w-5 h-5 text-gray-600" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  <SelectItem value="protesis">Prótesis Capilares</SelectItem>
                  <SelectItem value="mantenimiento">Productos de Mantenimiento</SelectItem>
                </SelectContent>
              </Select>
              
              {category === 'mantenimiento' && (
                <Select value={subcategory} onValueChange={setSubcategory}>
                  <SelectTrigger className="w-full border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las subcategorías</SelectItem>
                    <SelectItem value="disolventes">1. Disolventes</SelectItem>
                    <SelectItem value="adhesivos_liquidos">2. Adhesivos Líquidos o en Crema</SelectItem>
                    <SelectItem value="adhesivos_doble_cara">3. Adhesivos de Doble Cara</SelectItem>
                    <SelectItem value="otros_mantenimiento">4. Otros Productos de Mantenimiento</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        {search && (
          <p className="text-sm text-gray-600 mb-4" data-testid="products-search-count">
            {filteredProducts.length === 0
              ? `Sin resultados para "${search}"`
              : `${filteredProducts.length} resultado${filteredProducts.length === 1 ? '' : 's'} para "${search}"`}
          </p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
          {filteredProducts.map((product) => (
            <div key={product.id} className="group cursor-pointer" onClick={() => navigate(`/producto/${product.id}`)}>
              <div className="relative overflow-hidden bg-gray-100 aspect-square mb-3 md:mb-4">
                <ProtectedImage
                  src={optimizeImage(product.images[0], 'catalog')}
                  alt={product.name}
                  className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                />
                {product.allow_backorder && product.stock === 0 && (
                  <span className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] md:text-xs font-bold uppercase px-1.5 md:px-2 py-0.5 md:py-1 rounded" data-testid={`preorder-badge-${product.id}`}>Pre-venta</span>
                )}
                {product.track_stock !== false && product.stock === 0 && !product.allow_backorder && (
                  <span className="absolute top-2 left-2 bg-gray-800 text-white text-[10px] md:text-xs font-bold uppercase px-1.5 md:px-2 py-0.5 md:py-1 rounded">Agotado</span>
                )}
              </div>
              <h3 className="font-semibold mb-1 md:mb-2 text-sm md:text-base line-clamp-2">{product.name}</h3>
              <p className="text-gray-600 text-xs md:text-sm mb-2 md:mb-3 line-clamp-2 hidden sm:block">{product.description}</p>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-base md:text-xl font-bold">{product.price.toFixed(2)}€</span>
                <span className="text-xs md:text-sm text-gray-600 group-hover:text-black">Ver más →</span>
              </div>
              {product.allow_backorder && product.availability_date && (
                <p className="text-[11px] md:text-xs text-orange-700 mt-1">Envío el {new Date(product.availability_date).toLocaleDateString('es-ES', {day:'numeric', month:'short'})}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
